import React from 'react';
import { find, isEmpty } from 'lodash';
import cn from 'classnames';
import { Drawer, Form, Core, Localization } from 'connex-cds';
import styled from 'styled-components';
import { cleanupLineItems } from '../../../../util/cleanupLineItems';
import { useCompanySetup, useReasonCodeSetup, useVehicleSetup, useVehicleTypeSetup } from './MasterDataProvider';
import { useTicketContext } from './TicketContext';

const Styled = styled.div`
  padding-top: 24px;
  .reason {
    border: var(--color-border-container);
    background-color: var(--color-background-container);
    margin-bottom: 10px;
    padding: 3px 10px;
    borderradius: 4px;
    cursor: pointer;

    &.active {
      background-color: #d9d9d9;
    }
  }
`;

export const DriverAcceptance = props => {
  const { translateObjects } = Localization.useTranslateObjects();
  const {
    Components: { SubmitButton },
    values,
    setFieldValue,
    errors,
  } = Form.useFormContext();
  const { closeDrawer, visible: drawerIsVisible } = Drawer.useDrawerContext();
  const { ticket } = useTicketContext();

  const vehicleSetup = useVehicleSetup();
  const reasonCodeSetup = useReasonCodeSetup();
  const companySetup = useCompanySetup();
  const vehicleTypeSetup = useVehicleTypeSetup();

  const vehicleType = React.useMemo(() => {
    const targetVehicle = find(vehicleSetup.data, { id: ticket?.vehicleId });
    const targetVehicleType = find(vehicleTypeSetup.data, { crn: targetVehicle?.vehicleType?.vehicleTypeRef });
    return targetVehicleType?.id;
  }, [vehicleSetup.data, vehicleTypeSetup.data, ticket?.vehicleId]);

  const activeReasons = React.useMemo(
    () =>
      reasonCodeSetup?.data
        ?.filter?.(d => d.type === 'SIGNATURE')
        ?.filter?.(reason => reason.status === 'ACTIVE')
        ?.filter?.(reason =>
          companySetup?.data?.isMulticountry === true
            ? reason?.countries?.includes(ticket?.origin?.address?.countryCode) || !reason?.countries?.length
            : reason
        )
        ?.filter?.(reason => reason?.vehicleTypes?.includes(vehicleType) || !reason?.vehicleTypes?.length) || [],
    [companySetup?.data?.isMulticountry, reasonCodeSetup?.data, ticket?.origin?.address?.countryCode, vehicleType]
  );

  const options = React.useMemo(() => {
    if (!activeReasons?.length) {
      return [];
    }

    const reasonCodes = translateObjects(activeReasons, {
      getStringId: x => `SETUP_REASON-CODE_${x}`.replace(/\s/gi, '_').toUpperCase(),
      getPath: 'id',
      setPath: 'label',
      defaultMessagePath: 'description',
    });

    if (!reasonCodes) {
      return activeReasons;
    }

    return reasonCodes.map(reason => ({
      id: reason?.crn,
      label: reason?.label ?? reason?.description ?? reason?.name,
      isDefault: reason?.isDefault,
    }));
  }, [activeReasons]);

  // using default option as a selected option
  const defaultOption = React.useMemo(() => {
    const [option] = options.filter(option => option.isDefault);
    return option;
  }, [options]);

  const disabled = React.useMemo(() => {
    const errorsToCheck = cleanupLineItems(errors, values, 'ticketEvents');
    return !isEmpty(errorsToCheck) || !values?.driverAcceptanceReason;
  }, [errors, values?.driverAcceptanceReason]);

  const handleSave = React.useCallback(
    reason => {
      const targetReason = find(reasonCodeSetup.data, { crn: reason });
      setFieldValue('driverAcceptanceReason', targetReason);
      setFieldValue('customerStatus', 'ACCEPTED');
      setFieldValue('signature', undefined);
    },
    [reasonCodeSetup.data, setFieldValue]
  );

  const renderOption = React.useCallback(
    option => (
      <div
        key={option.id}
        data-reason-id={`${option.id}`}
        className={cn('reason', { active: values?.driverAcceptanceReason?.crn === option.id })}
        onClick={() => handleSave(option.id)}
      >
        {option.label}
      </div>
    ),
    [values?.driverAcceptanceReason, handleSave]
  );

  React.useEffect(() => {
    const isDriverAcceptanceReasonUndefined = values?.driverAcceptanceReason === undefined;
    const isDriverAcceptanceReasonEmptyString = values?.driverAcceptanceReason === '';
    const isDriverAcceptanceReasonValidValue = isDriverAcceptanceReasonUndefined || isDriverAcceptanceReasonEmptyString;
    if (isDriverAcceptanceReasonValidValue && !!defaultOption && drawerIsVisible) {
      document.querySelector(`[data-reason-id="${defaultOption?.id}"]`)?.click();
    }
  }, [setFieldValue, values?.driverAcceptanceReason, drawerIsVisible]);

  return (
    <Styled className={cn('driver-acceptance')}>
      <Core.Spinner spin={vehicleSetup.isLoading || reasonCodeSetup.isLoading}>
        {options.map(renderOption)}

        <SubmitButton
          stringId="submitAcceptance"
          data-testid="submit-driver-acceptance-button"
          onClick={closeDrawer}
          disabled={disabled}
        />
      </Core.Spinner>
    </Styled>
  );
};
